import { RichTranslator, useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';

import ConnectionErrorModal from 'components/modals/ConnectionErrorModal';
import EgnyteDomainModal from 'components/modals/EgnyteDomainModal';
import { getAccountShortName } from 'constants/integrations';
import AppStoreContext from 'contexts/AppStoreContext';
import { product } from 'copy/copy';
import { accountNames, integrationsPageText } from 'copy/integrationsPage';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import boxModalBanner from 'images/box-connection.jpg';
import box from 'images/box.svg';
import dropboxModalBanner from 'images/dropbox-connection.jpg';
import dropbox from 'images/dropbox.svg';
import egnyteModalBanner from 'images/egnyte-connection.jpg';
import egnyte from 'images/egnyte.svg';
import googleDriveModalBanner from 'images/google-drive-connection.jpg';
import googleDrive from 'images/google-drive.svg';
import onedriveModalBanner from 'images/onedrive-connection.jpg';
import onedrive from 'images/onedrive.svg';
import sharepointModalBanner from 'images/sharepoint-connection.png';
import sharepoint from 'images/sharepoint.svg';
import ConnectedAccounts from 'pages/IntegrationsPage/Integrations/ConnectedAccounts';
import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { ModalState } from 'records/ModalState';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { getAppRegion } from 'utils/getAppRegion';
import { AppRegion } from 'constants/appRegion';
import BackButton from '../../components/BackButton';
import IntegrationsCard from './Integrations/IntegrationsCard';

interface Props extends WithAppStoreProps, RouteComponentProps {}

interface ProviderInfo {
  providerError: string;
  provider: string;
  providerCapitalized: string;
}

const IntegrationsPage = (props: Props) => {
  const { accessToken } = useAuth();
  const history = useHistory();
  const [connectedAccountsLoaded, setConnectedAccountsLoaded] =
    useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [providerInfo, setProviderInfo] = useState<ProviderInfo>({
    providerError: '',
    provider: '',
    providerCapitalized: '',
  });

  const { state, actions } = useContext(AppStoreContext);

  const { translate } = useTranslator(['integrationsPage']);

  useEffect(() => {
    getConnectedAccounts();
    checkProviderError();
  }, []);

  const getContentForProviderError = (
    providerError: string,
    providerName: string
  ) => {
    if (
      providerError === 'invalid_scope' &&
      providerName === accountNames.short.sharepoint
    ) {
      return (
        <div className="modal__connection_error__content-light-text mb2">
          {translate(
            'integrationsPage:connectionError.invalidScope.firstParagraph',
            {
              providerName,
            }
          )}
        </div>
      );
    }
    if (providerError === 'access_denied') {
      return (
        <div className="modal__connection_error__content-light-text mb2">
          {translate(
            'integrationsPage:connectionError.accessDenied.firstParagraph',
            {
              providerName,
            }
          )}
        </div>
      );
    }

    return (
      <div>
        <div className="modal__connection_error__content-light-text mb2">
          {translate(
            'integrationsPage:connectionError.default.firstParagraph',
            {
              providerName,
            }
          )}
        </div>
        <div className="modal__connection_error__content-light-text">
          <RichTranslator
            components={[
              // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label
              <a
                href={integrationsPageText.technicalSupportLink}
                className="modal__connection_error__content-light-text"
              />,
            ]}
            translationKey="integrationsPage:connectionError.default.secondParagraph"
            variables={{
              providerError,
            }}
          />
        </div>
      </div>
    );
  };

  const getConnectedAccounts = async (): Promise<void> => {
    await actions.getConnectedAccounts();
    setConnectedAccountsLoaded(true);
  };

  const checkProviderError = async () => {
    const providerError = new URLSearchParams(props.location.search).get(
      'error'
    );
    const provider = localStorage.getItem('started-connection');
    if (provider && providerError) {
      const providerCapitalized =
        getAccountShortName(provider) === 'Account'
          ? translate('integrationsPage:accountDefaultShortName')
          : getAccountShortName(provider);

      const providerData = {
        providerError,
        provider,
        providerCapitalized,
      };

      setProviderInfo(providerData);
      setShowErrorModal(true);
    }
  };
  const onBackClick = () => {
    history.replace(`/project/${state.selectedProject}/settings`);
  };

  const {
    [FeatureFlag.DmsIntegrationBox]: isBoxIntegrationEnabled,
    [FeatureFlag.DmsIntegrationDropbox]: isDropboxIntegrationEnabled,
    [FeatureFlag.DmsIntegrationEgnyte]: isEgnyteIntegrationEnabled,
    [FeatureFlag.DmsIntegrationGoogleDrive]: isGoogleDriveIntegrationEnabled,
    [FeatureFlag.DmsIntegrationOneDrive]: isOneDriveIntegrationEnabled,
    [FeatureFlag.DmsIntegrationSharePoint]: isSharePointIntegrationEnabled,
  } = useFeatureFlags();

  const isUSRegion = getAppRegion() === AppRegion.US;

  const accessTokenParam = `?accessToken=${accessToken}`;
  const callback = `&callbackPath=/project/${state.selectedProject}/project-settings/integrations`;
  const projectId = `&projectId=${state.selectedProject}`;

  const sharePointAuthUrl = `${integrationsPageText.sharepoint.authUrl}${accessTokenParam}${callback}${projectId}`;
  const onedriveAuthUrl = `${integrationsPageText.onedrive.authUrl}${accessTokenParam}${callback}${projectId}`;
  const boxAuthUrl = `${integrationsPageText.box.authUrl}${accessTokenParam}${callback}${projectId}`;
  const dropboxAuthUrl = `${integrationsPageText.dropbox.authUrl}${accessTokenParam}${callback}${projectId}`;
  const googleDriveAuthUrl = `${integrationsPageText.googledrive.authUrl}${accessTokenParam}${callback}${projectId}`;
  const egnyteAuthUrl = `${integrationsPageText.egnyte.authUrl}${accessTokenParam}${callback}${projectId}`;

  return (
    <div className="p-5">
      {showErrorModal && (
        <ConnectionErrorModal
          header={`${providerInfo.providerCapitalized} Connection Error`}
          content={getContentForProviderError(
            providerInfo.providerError,
            providerInfo.providerCapitalized
          )}
          onClose={() => setShowErrorModal(false)}
        />
      )}

      <div className="bb-2">
        <BackButton onBackClick={onBackClick} />

        <div className="font-size-5 leading-5 medium  pt3">
          {translate('integrationsPage:title')}
        </div>
        <p
          className="font-size-5 font-normal m0 leading-[22px]"
          style={{ paddingBottom: '18px', paddingTop: '10px' }}
        >
          {translate('integrationsPage:shortText')}
        </p>
      </div>

      <div
        className="flex content-stretch"
        style={{
          maxWidth: '1024px',
          margin: isBoxIntegrationEnabled ? 0 : '0 auto',
          minWidth: '560px',
        }}
      >
        <div className="column w66 h100 br-2">
          <div className="b-light-grey-1">
            <div
              className="b-3 bgd-light-grey-2 mx4 my3 ml-0"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(277px, 1fr))',
                gridGap: '23px',
                padding: '23px',
              }}
            >
              {isSharePointIntegrationEnabled && (
                <IntegrationsCard
                  analyticsId="sharepoint-integration"
                  icon={sharepoint}
                  modalBanner={sharepointModalBanner}
                  content={integrationsPageText.sharepoint}
                  getAuthUrl={async () => sharePointAuthUrl}
                />
              )}
              {isOneDriveIntegrationEnabled && (
                <IntegrationsCard
                  analyticsId="onedrive-integration"
                  icon={onedrive}
                  modalBanner={onedriveModalBanner}
                  content={integrationsPageText.onedrive}
                  getAuthUrl={async () => onedriveAuthUrl}
                />
              )}
              {isBoxIntegrationEnabled && (
                <IntegrationsCard
                  analyticsId="box-integration"
                  icon={box}
                  modalBanner={boxModalBanner}
                  content={integrationsPageText.box}
                  getAuthUrl={async () => boxAuthUrl}
                />
              )}
              {isDropboxIntegrationEnabled && (
                <IntegrationsCard
                  analyticsId="dropbox-integration"
                  icon={dropbox}
                  modalBanner={dropboxModalBanner}
                  content={integrationsPageText.dropbox}
                  getAuthUrl={async () => dropboxAuthUrl}
                />
              )}
              {isGoogleDriveIntegrationEnabled && (
                <IntegrationsCard
                  analyticsId="googledrive-integration"
                  icon={googleDrive}
                  modalBanner={googleDriveModalBanner}
                  content={integrationsPageText.googledrive}
                  getAuthUrl={async () => googleDriveAuthUrl}
                />
              )}
              {isUSRegion && isEgnyteIntegrationEnabled && (
                <IntegrationsCard
                  analyticsId="egnyte-integration"
                  icon={egnyte}
                  modalBanner={egnyteModalBanner}
                  content={integrationsPageText.egnyte}
                  getAuthUrl={async () => {
                    return new Promise((resolve) => {
                      const onSubmit = (domain: string) => {
                        resolve(`${egnyteAuthUrl}&domainName=${domain}`);
                      };

                      const modalState = new ModalState({
                        id: 'enterEgnyteDomain',
                        portal: 'main',
                        open: true,
                        component: EgnyteDomainModal,
                        props: {
                          onSubmit,
                        },
                      });
                      actions.createModal(modalState);
                    });
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="column w33">
          <div className="color-gray-7 font-size-5 medium mt6 ml3">
            {translate('integrationsPage:sideBarTitle')}
          </div>
          <div
            id="connected-account-list"
            className="b-3 bgd-light-grey-2 m3 column pb3 oh"
          >
            {connectedAccountsLoaded ? (
              <ConnectedAccounts accounts={state.connectedAccounts} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAppStore<Props>(IntegrationsPage);
